import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';
import './NotificationAnalytics.css';

interface OverviewStats {
  total: number;
  sent: number;
  pending: number;
  failed: number;
  read: number;
}

interface ChannelStat {
  channel: string;
  total: number;
  sent: number;
  failed: number;
}

interface TypeStat {
  notification_type: string;
  total: number;
  sent: number;
  read: number;
}

interface DailyTrend {
  date: string;
  total: number;
  sent: number;
  failed: number;
}

interface FailedNotification {
  notification_id: string;
  user_id: string;
  notification_type: string;
  channel: string;
  subject: string;
  message: string;
  error_message: string;
  created_at: string;
  email: string;
  first_name: string;
  last_name: string;
}

interface ChannelComparison {
  channel: string;
  total: number;
  sent: number;
  failed: number;
  success_rate: number;
  avg_delivery_seconds: number;
}

export const NotificationAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState(30);
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [channelStats, setChannelStats] = useState<ChannelStat[]>([]);
  const [typeStats, setTypeStats] = useState<TypeStat[]>([]);
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([]);
  const [channelComparison, setChannelComparison] = useState<ChannelComparison[]>([]);
  const [failedNotifications, setFailedNotifications] = useState<FailedNotification[]>([]);
  const [avgDeliverySeconds, setAvgDeliverySeconds] = useState(0);
  const [readRate, setReadRate] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [retryingId, setRetryingId] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch overview stats
      const overviewResponse = await fetch(
        `${API_URL}/notification-analytics/overview?days=${timeRange}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const overviewResult = await overviewResponse.json();

      if (overviewResult.success && overviewResult.data) {
        setOverview(overviewResult.data.overview);
        setChannelStats(overviewResult.data.by_channel);
        setTypeStats(overviewResult.data.by_type);
        setDailyTrend(overviewResult.data.daily_trend);
        setAvgDeliverySeconds(overviewResult.data.avg_delivery_seconds);
        setReadRate(overviewResult.data.read_rate);
      }

      // Fetch channel comparison
      const channelResponse = await fetch(
        `${API_URL}/notification-analytics/channels/comparison?days=${timeRange}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const channelResult = await channelResponse.json();

      if (channelResult.success && channelResult.data) {
        setChannelComparison(channelResult.data.channels);
      }

      // Fetch failed notifications
      const failedResponse = await fetch(
        `${API_URL}/notification-analytics/failed?limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const failedResult = await failedResponse.json();

      if (failedResult.success && failedResult.data) {
        setFailedNotifications(failedResult.data.notifications);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryNotification = async (notificationId: string) => {
    try {
      setRetryingId(notificationId);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${API_URL}/notification-analytics/notifications/${notificationId}/retry`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await response.json();
      if (result.success) {
        setSuccessMessage('Notification queued for retry');
        fetchAnalytics();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.message || 'Failed to retry notification');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to retry notification');
    } finally {
      setRetryingId(null);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return '📧';
      case 'sms':
        return '📱';
      case 'in_app':
        return '🔔';
      default:
        return '📬';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${(seconds / 60).toFixed(1)}m`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="notification-analytics">
      <div className="page-header">
        <div className="header-content">
          <h1>📊 Notification Analytics</h1>
          <p className="header-subtitle">Monitor notification delivery and performance metrics</p>
        </div>
        <div className="header-actions">
          <select
            className="time-range-selector"
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">
            Back to Dashboard
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>✓ {successMessage}</span>
        </div>
      )}

      {/* Overview Stats */}
      {overview && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📬</div>
            <div className="stat-content">
              <h3>Total Notifications</h3>
              <p className="stat-value">{overview.total.toLocaleString()}</p>
              <p className="stat-sub">in last {timeRange} days</p>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>Successfully Sent</h3>
              <p className="stat-value">{overview.sent.toLocaleString()}</p>
              <p className="stat-sub">
                {((overview.sent / overview.total) * 100).toFixed(1)}% success rate
              </p>
            </div>
          </div>
          <div className="stat-card failed">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <h3>Failed</h3>
              <p className="stat-value">{overview.failed.toLocaleString()}</p>
              <p className="stat-sub">
                {((overview.failed / overview.total) * 100).toFixed(1)}% failure rate
              </p>
            </div>
          </div>
          <div className="stat-card read">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <h3>Read Rate</h3>
              <p className="stat-value">{readRate.toFixed(1)}%</p>
              <p className="stat-sub">
                {overview.read.toLocaleString()} read
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Channel Performance */}
      <div className="analytics-section">
        <h2>Channel Performance</h2>
        <div className="channel-grid">
          {channelComparison.map((channel) => (
            <div key={channel.channel} className="channel-card">
              <div className="channel-header">
                <span className="channel-icon">{getChannelIcon(channel.channel)}</span>
                <h3>{channel.channel.toUpperCase()}</h3>
              </div>
              <div className="channel-stats">
                <div className="stat-row">
                  <span className="label">Total:</span>
                  <span className="value">{channel.total.toLocaleString()}</span>
                </div>
                <div className="stat-row">
                  <span className="label">Success Rate:</span>
                  <span className="value success">{channel.success_rate.toFixed(1)}%</span>
                </div>
                <div className="stat-row">
                  <span className="label">Failed:</span>
                  <span className="value failed">{channel.failed.toLocaleString()}</span>
                </div>
                <div className="stat-row">
                  <span className="label">Avg Delivery:</span>
                  <span className="value">{formatDuration(channel.avg_delivery_seconds)}</span>
                </div>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${channel.success_rate}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notification Types */}
      <div className="analytics-section">
        <h2>By Notification Type</h2>
        <div className="type-table">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Total</th>
                <th>Sent</th>
                <th>Read</th>
                <th>Read Rate</th>
              </tr>
            </thead>
            <tbody>
              {typeStats.map((type) => (
                <tr key={type.notification_type}>
                  <td className="type-name">{getNotificationTypeLabel(type.notification_type)}</td>
                  <td>{type.total.toLocaleString()}</td>
                  <td>{type.sent.toLocaleString()}</td>
                  <td>{type.read.toLocaleString()}</td>
                  <td>
                    <span className="read-rate">
                      {((type.read / type.sent) * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Trend */}
      <div className="analytics-section">
        <h2>Daily Trend (Last 30 Days)</h2>
        <div className="trend-chart">
          <div className="chart-container">
            {dailyTrend.slice(0, 15).reverse().map((day, index) => {
              const maxValue = Math.max(...dailyTrend.map(d => d.total));
              const height = (day.total / maxValue) * 100;
              const successRate = day.total > 0 ? (day.sent / day.total) * 100 : 0;

              return (
                <div key={day.date} className="chart-bar-group">
                  <div className="chart-bar-container">
                    <div
                      className={`chart-bar ${successRate < 90 ? 'warning' : ''}`}
                      style={{ height: `${height}%` }}
                      title={`${day.total} notifications (${successRate.toFixed(1)}% success)`}
                    >
                      <span className="bar-value">{day.total}</span>
                    </div>
                  </div>
                  <div className="chart-label">{formatDate(day.date)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Failed Notifications */}
      {failedNotifications.length > 0 && (
        <div className="analytics-section">
          <h2>Recent Failed Notifications</h2>
          <div className="failed-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>Subject</th>
                  <th>Error</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {failedNotifications.map((notification) => (
                  <tr key={notification.notification_id}>
                    <td>{formatDateTime(notification.created_at)}</td>
                    <td>
                      <div className="user-cell">
                        <div className="user-name">
                          {notification.first_name} {notification.last_name}
                        </div>
                        <div className="user-email">{notification.email}</div>
                      </div>
                    </td>
                    <td>{getNotificationTypeLabel(notification.notification_type)}</td>
                    <td>
                      <span className="channel-badge">
                        {getChannelIcon(notification.channel)} {notification.channel}
                      </span>
                    </td>
                    <td className="subject-cell" title={notification.subject}>
                      {notification.subject.substring(0, 40)}
                      {notification.subject.length > 40 && '...'}
                    </td>
                    <td className="error-cell" title={notification.error_message}>
                      {notification.error_message?.substring(0, 50)}
                      {notification.error_message && notification.error_message.length > 50 && '...'}
                    </td>
                    <td>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRetryNotification(notification.notification_id)}
                        loading={retryingId === notification.notification_id}
                        disabled={!!retryingId}
                      >
                        Retry
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
